
import React, { useState, useEffect } from 'react';
import Button from '../components/Button';
import { ARTIFACT_URL, LOADING_MESSAGES } from '../constants';
import { fetchLockedImageBase64, generateOlekMeme } from '../services/geminiService';
import { saveToGallery, subscribeToGallery, likeGalleryImage, getCurrentUsername } from '../services/firebase';
import { Download, Eraser, PenTool, Sparkles, Grid, Check, ArrowRight, Heart } from 'lucide-react';

const Forge: React.FC = () => {
  // Tabs: 'mint' or 'vault'
  const [activeTab, setActiveTab] = useState<'mint' | 'vault'>('mint');

  // Generator State
  const [prompt, setPrompt] = useState('');
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [base64Source, setBase64Source] = useState<string | null>(null);
  const [loadingMsg, setLoadingMsg] = useState(LOADING_MESSAGES[0]);
  const [currentUser, setCurrentUser] = useState('');

  // Gallery State
  const [galleryImages, setGalleryImages] = useState<any[]>([]);

  useEffect(() => {
    setCurrentUser(getCurrentUsername());

    fetchLockedImageBase64(ARTIFACT_URL)
      .then(setBase64Source)
      .catch(err => {
        console.error(err);
        setError("asset seizure by the feds.");
      });
      
    // Subscribe to gallery (Memes only)
    const unsubscribe = subscribeToGallery(setGalleryImages);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let interval: any;
    if (isLoading) {
      interval = setInterval(() => {
        setLoadingMsg(LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)]);
      }, 1500);
    }
    return () => clearInterval(interval);
  }, [isLoading]);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || !base64Source) return;

    setIsLoading(true);
    setError(null);
    setGeneratedImage(null);

    try {
      // 1. Generate
      const result = await generateOlekMeme(prompt, base64Source);
      setGeneratedImage(result);
      
      // 2. Auto Save to Vault
      saveToGallery(result, prompt, 'meme');
      
    } catch (err: any) {
      setError(err.message || "printer jammed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLike = (id: string) => {
    likeGalleryImage(id);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between border-b-4 border-black pb-4">
        <div>
          <h1 className="text-4xl font-black lowercase">Money Printer</h1>
          <p className="text-gray-500 font-bold lowercase">generate content for the brand</p>
        </div>
        <div className="hidden md:block">
           <div className="w-16 h-16 bg-olek-pink text-white rounded-full border-4 border-black flex items-center justify-center font-bold text-xs text-center p-2 rotate-12 shadow-hard-sm">
             <Sparkles size={24} />
           </div>
        </div>
      </div>

      {/* Sub-Nav Buttons */}
      <div className="flex gap-4">
        <button 
          onClick={() => setActiveTab('mint')}
          className={`flex-1 py-2 font-bold lowercase text-lg border-2 border-black transition-all ${activeTab === 'mint' ? 'bg-black text-white shadow-hard-sm' : 'bg-white text-black opacity-40 hover:opacity-100 hover:bg-gray-100'}`}
        >
          <PenTool size={18} className="inline mr-2 mb-1" /> Mint New
        </button>
        <button 
          onClick={() => setActiveTab('vault')}
          className={`flex-1 py-2 font-bold lowercase text-lg border-2 border-black transition-all ${activeTab === 'vault' ? 'bg-black text-white shadow-hard-sm' : 'bg-white text-black opacity-40 hover:opacity-100 hover:bg-gray-100'}`}
        >
          <Grid size={18} className="inline mr-2 mb-1" /> The Vault
        </button>
      </div>

      {/* MINT VIEW */}
      {activeTab === 'mint' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 animate-in fade-in slide-in-from-bottom-4 duration-300">
          {/* Controls */}
          <div className="space-y-6">
             <div className="bg-green-100 p-4 border-4 border-black shadow-hard-sm rotate-[-1deg]">
               <div className="flex gap-4 items-center">
                  <img src={ARTIFACT_URL} className="w-16 h-16 border-2 border-black bg-white" alt="ref" />
                  <div className="leading-tight">
                    <p className="font-bold">Model: Olek.png</p>
                    <p className="text-sm">Hedonistic. Rich. Always winning.</p>
                  </div>
               </div>
             </div>

             <form onSubmit={handleGenerate} className="space-y-4">
               <div>
                 <label className="block font-bold text-xl mb-2 lowercase">what is he doing now?</label>
                 <textarea
                   value={prompt}
                   onChange={(e) => setPrompt(e.target.value)}
                   placeholder="e.g. buying a yacht, laughing at wagie wojak, wearing his pink suit at a gala..."
                   className="w-full h-40 border-4 border-black p-4 font-comic text-xl outline-none focus:bg-gray-50 resize-none shadow-hard-sm"
                 />
                 <p className="text-right text-xs font-bold text-gray-400 mt-2">signed as: {currentUser}</p>
               </div>
               
               <Button 
                 type="submit" 
                 disabled={isLoading || !base64Source} 
                 className="w-full py-4 text-xl flex items-center justify-center gap-2 bg-olek-pink hover:bg-olek-pink-dark text-white"
               >
                 {isLoading ? (
                   <span>{loadingMsg}</span>
                 ) : (
                   <>
                     <PenTool size={24} /> mint meme
                   </>
                 )}
               </Button>
             </form>
          </div>

          {/* Canvas / Result */}
          <div className="relative min-h-[500px] h-auto bg-white border-4 border-black shadow-hard flex flex-col items-center justify-center p-4">
             {/* Tape Effect */}
             <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-32 h-8 bg-olek-pink/50 rotate-[-2deg] z-10"></div>

             {error && (
               <div className="text-center space-y-2 max-w-xs">
                 <Eraser size={48} className="mx-auto text-alert" />
                 <p className="font-bold text-alert">{error}</p>
               </div>
             )}

             {!isLoading && !error && !generatedImage && (
               <div className="text-center opacity-20 select-none">
                 <p className="text-8xl font-black">$</p>
                 <p className="text-xl font-bold">canvas empty</p>
               </div>
             )}

             {isLoading && (
               <div className="animate-spin text-6xl">
                 💸
               </div>
             )}

             {generatedImage && (
               <div className="w-full h-full relative group flex flex-col gap-4">
                 <img src={generatedImage} alt="Generated" className="w-full h-auto object-contain bg-gray-50 border-2 border-black" />
                 
                 <div className="flex items-center justify-center gap-2 bg-green-100 text-green-800 px-3 py-1 border-2 border-green-800 text-sm font-bold mx-auto w-fit rotate-[-2deg]">
                   <Check size={16} /> saved to vault automatically
                 </div>

                 <div className="grid grid-cols-2 gap-4">
                   <a href={generatedImage} download={`olek_meme_${Date.now()}.png`} className="w-full">
                     <Button variant="secondary" className="text-sm py-3 w-full flex items-center justify-center">
                       <Download size={18} className="inline mr-2" /> download
                     </Button>
                   </a>
                   <Button 
                     onClick={() => setActiveTab('vault')} 
                     variant="black"
                     className="text-sm py-3 w-full flex items-center justify-center"
                   >
                     view in vault <ArrowRight size={18} className="inline ml-2" />
                   </Button>
                 </div>
               </div>
             )}
          </div>
        </div>
      )}

      {/* VAULT VIEW (Gallery) */}
      {activeTab === 'vault' && (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
           {galleryImages.length === 0 ? (
            <div className="text-center py-20 text-gray-400 font-bold text-xl border-4 border-dashed border-gray-300 rounded-lg">
              vault is empty. <br/> start minting to fill it.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {galleryImages.map((img, i) => (
                <div key={img.id} className="bg-white p-2 border-4 border-black shadow-hard group relative">
                  <div className="aspect-square bg-gray-100 overflow-hidden border-2 border-black">
                    <img src={img.imageUrl} alt="meme" className="w-full h-full object-cover" />
                  </div>
                  
                  {/* Metadata */}
                  <div className="mt-2 space-y-1">
                     <p className="text-xs font-bold opacity-50 truncate">"{img.prompt}"</p>
                     <div className="flex justify-between items-center">
                       <span className="text-xs font-bold text-olek-pink-dark">@{img.authorName || 'anon'}</span>
                       <button 
                         onClick={() => handleLike(img.id)}
                         className="flex items-center gap-1 text-xs font-bold bg-gray-100 px-2 py-1 rounded hover:bg-pink-100 transition-colors border border-gray-200"
                       >
                         <Heart size={12} className={localStorage.getItem(`liked_${img.id}`) ? "fill-red-500 text-red-500" : ""} />
                         {img.likes || 0}
                       </button>
                     </div>
                  </div>
                  
                  {/* Overlay Actions */}
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                     <div className="pointer-events-auto">
                        <a href={img.imageUrl} download={`olek_art_${i}.png`}>
                          <Button variant="secondary" className="shadow-lg scale-90">
                            <Download size={16} />
                          </Button>
                        </a>
                     </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default Forge;
